// Shell-level contextual side panel (operator-shell refactor PR2).
//
// One panel owned by the shell, its content driven by the active section
// (sectionForPath). Replaces page-level secondary navigation so the
// "what exists" surface is consistent and not duplicated. It currently owns Home, Chat,
// Brand Docs, Workflows, Assets, and Analytics; other sections still render their
// own page sidebar until they are migrated (see SHELL_PANEL_SECTIONS), so the panel
// is null elsewhere.

import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useActiveBrand } from "../../hooks/useActiveBrand";
import { useBrand } from "../../hooks/useBrand";
import { useAgentThreads } from "../../hooks/useAgent";
import { useTaskCatalog } from "../../hooks/useTaskCatalog";
import { useBrandDocsList, type BrandDoc } from "../../hooks/useBrandDocsList";
import { useCollections } from "../../hooks/useMedia";
import { useBrandAnalyticsProperties } from "../../hooks/useAnalytics";
import { connectionPeriodSearch, readPeriod, scopedAnalyticsHref } from "../../lib/analytics-period";
import { useWorkspaceTabs } from "../../contexts/WorkspaceTabsContext";
import { buildBrandScopedPath, parseBrandIdFromPath } from "../../lib/brand-navigation";
import { parseTaskFilters, TASK_CATEGORY_LABELS } from "../../lib/task-filters";
import { buildAnalyticsPropertyWorkTab, buildAssetsWorkTab } from "../../lib/shell-routes";
import { SHELL_PANEL_SECTIONS, isInShellRoute, sectionForPath } from "../../lib/workspace-sections";
import type { WorkTab } from "../../lib/workspace-tabs";

const PANEL_CLASS = "flex h-auto w-full flex-col border-b border-slate-200 bg-slate-50 md:h-full md:w-[252px] md:border-b-0 md:border-r";
const EYEBROW = "font-data text-[11px] font-medium uppercase tracking-[0.16em] text-slate-500";

/** Returns the contextual side panel for the current section, or null when the
 *  section is not yet shell-owned (those pages keep their own sidebar). */
export function ShellSidePanel() {
  const location = useLocation();
  const section = sectionForPath(location.pathname);
  // SHELL_PANEL_SECTIONS is the single source for which sections are shell-owned;
  // each member must have a case below.
  if (!SHELL_PANEL_SECTIONS.has(section)) return null;
  if (section === "home") return <HomePanel />;
  if (section === "chat") return <ChatPanel />;
  if (section === "tasks") return <TasksPanel />;
  if (section === "activity") return <ActivityPanel />;
  if (section === "docs") return <DocsPanel />;
  if (section === "workflows") return <WorkflowsPanel />;
  if (section === "assets") return <AssetsPanel />;
  if (section === "analytics") return <AnalyticsPanel />;
  return null;
}

/** Tasks owns no independent browser state: these URL-backed filters are the same
 * controls the page consumes, so deep links, mobile context, and desktop shell agree. */
function TasksPanel() {
  const location = useLocation();
  const navigate = useNavigate();
  const { activeBrand } = useActiveBrand();
  const { data, isLoading, status, refetch } = useTaskCatalog(activeBrand?.id ?? undefined);
  const tasks = data?.data ?? [];
  const { category } = parseTaskFilters(location.search);

  function setFilter(next: { category: string }) {
    const updated = new URLSearchParams(location.search);
    updated.delete("view");
    const nextCategory = next.category;
    if (nextCategory === "all") updated.delete("category"); else updated.set("category", nextCategory);
    const search = updated.toString();
    navigate(search ? `/tasks?${search}` : "/tasks", { replace: true });
  }

  return (
    <aside className={PANEL_CLASS}>
      <div className="border-b border-slate-200 px-4 py-4">
        <p className={EYEBROW}>Actions</p>
        <h2 className="mt-1 text-xl font-semibold text-slate-900">Choose what to run</h2>
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto px-3 py-4">
        <div className="mb-2 flex items-center justify-between"><p className={EYEBROW}>Categories</p><span className="font-data text-[11px] text-slate-400">{Object.keys(TASK_CATEGORY_LABELS).length}</span></div>
        {isLoading ? <p className="px-3 py-2 text-sm text-slate-400">Loading Actions…</p>
          : status === "error" ? <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900"><p>Actions could not be loaded.</p><button type="button" onClick={() => void refetch()} className="mt-2 font-medium underline underline-offset-2">Retry Actions</button></div>
          : <div className="space-y-1">
            <button type="button" onClick={() => setFilter({ category: "all" })} className={`w-full rounded-lg px-3 py-2 text-left text-sm transition ${category === "all" ? "bg-white font-medium text-slate-900 shadow-sm ring-1 ring-slate-200" : "text-slate-700 hover:bg-white/70"}`}>All categories</button>
            {Object.entries(TASK_CATEGORY_LABELS).map(([key, label]) => <button key={key} type="button" onClick={() => setFilter({ category: key })} className={`w-full rounded-lg px-3 py-2 text-left text-sm transition ${category === key ? "bg-white font-medium text-slate-900 shadow-sm ring-1 ring-slate-200" : "text-slate-700 hover:bg-white/70"}`}>
              {label}<span className="float-right font-data text-[11px] text-slate-400">{tasks.filter((task) => task.category === key).length}</span>
            </button>)}
          </div>}
      </div>
    </aside>
  );
}

/** Results retain Runs context; this panel contains only real recovery navigation. */
function ActivityPanel() {
  const navigate = useNavigate();
  const { activeBrand } = useActiveBrand();
  return (
    <aside className={PANEL_CLASS}>
      <div className="border-b border-slate-200 px-4 py-4">
        <p className={EYEBROW}>Runs</p>
        <h2 className="mt-1 text-xl font-semibold text-slate-900">Result</h2>
      </div>
      <div className="space-y-4 px-4 py-4 text-sm leading-6 text-slate-600">
        <p>Review what this Run produced, then return to Runs when you are ready to compare or continue work.</p>
        <button
          type="button"
          onClick={() => navigate(buildBrandScopedPath("/runs", activeBrand?.id ?? null))}
          className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-left font-medium text-slate-700 hover:bg-slate-50"
        >
          Back to Runs
        </button>
      </div>
    </aside>
  );
}

/**
 * Asset collections are the durable browser entry points already provided by the media API.
 * This deliberately links into the existing library instead of duplicating its filters or
 * upload controls in the panel.
 */
function AssetsPanel() {
  const location = useLocation();
  const { activeBrand } = useActiveBrand();
  const { openTab } = useWorkspaceTabs();
  const brandId = parseBrandIdFromPath(location.pathname) ?? activeBrand?.id ?? null;
  const { data, isLoading, status, refetch } = useCollections(brandId ?? undefined);
  const collections = data?.data ?? [];

  function openLibrary(collectionId?: string) {
    if (!brandId) return;
    const tab = buildAssetsWorkTab(brandId, collectionId);
    openTab(tab);
  }

  return (
    <aside className={PANEL_CLASS}>
      <div className="border-b border-slate-200 px-4 py-4">
        <p className={EYEBROW}>Assets</p>
        <h2 className="mt-1 text-xl font-semibold text-slate-900">Asset library</h2>
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto px-3 py-4">
        <button type="button" onClick={() => openLibrary()} className="mb-5 w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-left text-sm font-medium text-slate-800 transition hover:bg-slate-50">
          Browse all assets
        </button>
        <div>
          <div className="mb-2 flex items-center justify-between">
            <p className={EYEBROW}>Collections</p>
            <span className="font-data text-[11px] text-slate-400">{isLoading ? "Loading…" : collections.length}</span>
          </div>
          {isLoading ? <p className="px-3 py-2 text-sm text-slate-400">Loading collections…</p>
            : status === "error" ? <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
              <p>Collections could not be loaded.</p>
              <button type="button" onClick={() => void refetch()} className="mt-2 text-sm font-medium text-amber-950 underline underline-offset-2">Retry collections</button>
            </div>
            : collections.length > 0 ? <div className="space-y-1">
            {collections.slice(0, 8).map((collection) => (
              <button key={collection.id} type="button" onClick={() => openLibrary(collection.id)} className="w-full rounded-lg px-3 py-2 text-left text-sm text-slate-700 transition hover:bg-white/70">
                <span className="block truncate font-medium text-slate-800">{collection.label}</span>
                <span className="font-data text-[11px] text-slate-400">{collection.asset_count} assets</span>
              </button>
            ))}
          </div> : <p className="px-3 py-2 text-sm text-slate-400">No collections yet.</p>}
        </div>
      </div>
    </aside>
  );
}

/** Properties are the analytics work objects; choosing one opens its existing stateful tab. */
function AnalyticsPanel() {
  const location = useLocation();
  const navigate = useNavigate();
  const { activeBrand } = useActiveBrand();
  const { openTab } = useWorkspaceTabs();
  const brandId = parseBrandIdFromPath(location.pathname) ?? activeBrand?.id ?? null;
  const search = new URLSearchParams(location.search);
  const [startDate, endDate] = readPeriod(search);
  const { data, isLoading, status, refetch } = useBrandAnalyticsProperties(brandId ?? undefined, startDate, endDate);
  const properties = data?.data ?? [];

  function openProperty(surfaceId: string, label: string) {
    if (!brandId) return;
    const tab = buildAnalyticsPropertyWorkTab(brandId, surfaceId, label);
    openTab(tab);
  }

  function openSource(surfaceId: string, assignmentId: string, capability: string) {
    if (!brandId) return;
    const restored = connectionPeriodSearch(brandId, assignmentId, search);
    if (capability !== "google.search_console.read") {
      for (const key of ["attention", "ctr_limit", "impressions_min"]) restored.delete(key);
    }
    navigate(scopedAnalyticsHref(`/brand/${encodeURIComponent(brandId)}/data-views`, restored, readPeriod(restored), {
      surface: surfaceId, source_assignment: assignmentId, view: capability === "google.search_console.read" ? "search-performance" : "website-traffic", report: capability === "google.search_console.read" ? "overview" : "acquisition", detail: null, page: null,
    }));
  }

  return (
    <aside className={PANEL_CLASS}>
      <div className="border-b border-slate-200 px-4 py-4">
        <p className={EYEBROW}>Analytics</p>
        <h2 className="mt-1 text-xl font-semibold text-slate-900">Properties</h2>
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto px-3 py-4">
        <button type="button" onClick={() => brandId && navigate(`/brand/${encodeURIComponent(brandId)}/data-views`)} className="mb-2 w-full rounded-lg border border-brand-200 bg-brand-50 px-3 py-2 text-left text-sm font-medium text-brand-900 transition hover:bg-brand-100">
          Open Data Views
        </button>
        <button type="button" onClick={() => brandId && navigate(`/brand/${encodeURIComponent(brandId)}/analytics`)} className="mb-5 w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-left text-sm font-medium text-slate-800 transition hover:bg-slate-50">
          View all properties
        </button>
        <div className="mb-2 flex items-center justify-between">
          <p className={EYEBROW}>Websites</p>
          <span className="font-data text-[11px] text-slate-400">{isLoading ? "Loading…" : properties.length}</span>
        </div>
        {isLoading ? <p className="px-3 py-2 text-sm text-slate-400">Loading properties…</p>
          : status === "error" ? <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
            <p>Properties could not be loaded.</p>
            <button type="button" onClick={() => void refetch()} className="mt-2 text-sm font-medium text-amber-950 underline underline-offset-2">Retry properties</button>
          </div>
          : properties.length > 0 ? <div className="space-y-1">
          {properties.map((property) => (
            <div key={property.surface_id}><button type="button" onClick={() => openProperty(property.surface_id, property.display_name)} className="w-full rounded-lg px-3 py-2 text-left text-sm text-slate-700 transition hover:bg-white/70">
              <span className="block truncate font-medium text-slate-800">{property.display_name}</span>
              <span className="font-data text-[11px] text-slate-400">{property.connected ? "Connected" : "Not connected"}</span>
            </button><ul className="ml-4 border-l border-slate-200 pl-2">{property.sources.filter(source => source.relationship === "native" && source.compatibility !== "mismatched" && ["google.search_console.read", "google.analytics.read"].includes(source.capability)).map(source => {
              const label = source.capability === "google.search_console.read" ? "Google Search Console" : "Google Analytics 4";
              const selected = search.get("surface") === property.surface_id && search.get("source_assignment") === source.assignment_id;
              return <li key={source.assignment_id}><button type="button" aria-current={selected ? "page" : undefined} onClick={() => openSource(property.surface_id, source.assignment_id, source.capability)} className={`w-full rounded-lg px-3 py-2 text-left text-xs ${selected ? "bg-brand-50 font-semibold text-brand-700" : "text-slate-700 hover:bg-white"}`}><span className="block">{label}</span>{source.resource_display_name && <span className="block truncate text-slate-500">{source.resource_display_name}</span>}</button></li>;
            })}</ul></div>
          ))}
        </div> : <p className="px-3 py-2 text-sm text-slate-400">No website properties yet.</p>}
      </div>
    </aside>
  );
}

/** Chat threads remain owned by the existing agent page; this panel is a lightweight
 * brand-scoped browser that selects a thread through the page's URL contract. */
function ChatPanel() {
  const location = useLocation();
  const navigate = useNavigate();
  const { activeBrand } = useActiveBrand();
  const brandId = parseBrandIdFromPath(location.pathname) ?? activeBrand?.id ?? null;
  const { threads, isLoading, status, refetch, deleteThread, archivingThreadId } = useAgentThreads(brandId);
  const routeBrandId = brandId ? encodeURIComponent(brandId) : null;
  const [archiveError, setArchiveError] = useState<string | null>(null);

  function openThread(threadId: string) {
    if (!routeBrandId) return;
    navigate(`/agent/${routeBrandId}?thread=${encodeURIComponent(threadId)}`);
  }

  async function archiveThread(threadId: string) {
    if (!routeBrandId) return;
    setArchiveError(null);
    const selectedThreadId = new URLSearchParams(location.search).get("thread");
    const nextThread = threads.find((thread) => thread.id !== threadId);
    try {
      await deleteThread(threadId);
      if (selectedThreadId === threadId) {
        navigate(nextThread ? `/agent/${routeBrandId}?thread=${encodeURIComponent(nextThread.id)}` : `/agent/${routeBrandId}?new=1`);
      }
    } catch {
      setArchiveError("Thread could not be archived. Try again.");
    }
  }

  return (
    <aside className={PANEL_CLASS}>
      <div className="border-b border-slate-200 px-4 py-4">
        <p className={EYEBROW}>Agent</p>
        <h2 className="mt-1 text-xl font-semibold text-slate-900">Chat threads</h2>
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto px-3 py-4">
        <button
          type="button"
          onClick={() => routeBrandId && navigate(`/agent/${routeBrandId}?new=1`)}
          className="mb-5 w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-left text-sm font-medium text-slate-800 transition hover:bg-slate-50"
        >
          Start a new chat
        </button>
        <div className="mb-2 flex items-center justify-between">
          <p className={EYEBROW}>Recent threads</p>
          <span className="font-data text-[11px] text-slate-400">{isLoading ? "Loading…" : threads.length}</span>
        </div>
        {isLoading ? <p className="px-3 py-2 text-sm text-slate-400">Loading threads…</p>
          : status === "error" ? <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
            <p>Threads could not be loaded.</p>
            <button type="button" onClick={() => void refetch()} className="mt-2 text-sm font-medium text-amber-950 underline underline-offset-2">Retry threads</button>
          </div>
          : threads.length > 0 ? <div className="space-y-1">
          {threads.map((thread) => (
            <div key={thread.id} className="flex items-center gap-1 rounded-lg transition hover:bg-white/70">
              <button type="button" onClick={() => openThread(thread.id)} className="min-w-0 flex-1 px-3 py-2 text-left text-sm text-slate-700">
                <span className="block truncate font-medium text-slate-800">{thread.title?.trim() || "Untitled chat"}</span>
                <span className="block truncate font-data text-[11px] text-slate-400">{thread.is_active ? "Working now" : thread.preview || "No messages yet"}</span>
              </button>
              <button type="button" aria-label={`Archive ${thread.title?.trim() || "chat"}`} onClick={() => void archiveThread(thread.id)} disabled={archivingThreadId === thread.id} className="mr-2 shrink-0 rounded px-2 py-1 text-xs text-slate-500 hover:bg-slate-100 hover:text-slate-800 disabled:cursor-wait disabled:opacity-60">
                {archivingThreadId === thread.id ? "Archiving…" : "Archive"}
              </button>
            </div>
          ))}
        </div> : <p className="px-3 py-2 text-sm text-slate-400">No chat threads yet.</p>}
        {archiveError && <p role="alert" className="mt-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">{archiveError}</p>}
      </div>
    </aside>
  );
}

function HomePanel() {
  const navigate = useNavigate();
  const { activeBrand } = useActiveBrand();
  const brandId = activeBrand?.id ?? null;
  const { tabs, activeId, openTab } = useWorkspaceTabs();
  const openWorkTabs = tabs.filter((tab) => tab.id !== "home");

  function openOrNavigate(tab: WorkTab) {
    if (isInShellRoute(tab.route)) openTab(tab);
    else navigate(tab.route);
  }

  // No active brand → main shows the welcome screen; don't render a disabled panel
  // beside it (matches the old WorkspacePage behavior).
  if (!brandId) return null;
  const routeBrandId = encodeURIComponent(brandId);

  return (
    <aside className={PANEL_CLASS}>
      <div className="border-b border-slate-200 px-4 py-4">
        <p className={EYEBROW}>Workspace</p>
        <h2 className="mt-1 text-xl font-semibold text-slate-900">Your brand</h2>
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto px-3 py-4">
        <div className="mb-5">
          <p className={EYEBROW}>Work</p>
          <button type="button" onClick={() => navigate(`/brand/${routeBrandId}/campaigns`)} className="mt-2 w-full rounded-lg px-3 py-2 text-left text-sm font-semibold text-brand-700 hover:bg-white/70">Campaigns</button>
          <div className="mt-2 grid gap-1">
            <button type="button" onClick={() => navigate(`/agent/${routeBrandId}`)} className="rounded-lg px-3 py-2 text-left text-sm text-slate-700 transition hover:bg-white/70">Chat</button>
            <button type="button" onClick={() => navigate(`/brand/${routeBrandId}/assets`)} className="rounded-lg px-3 py-2 text-left text-sm text-slate-700 transition hover:bg-white/70">Asset library</button>
            <button type="button" onClick={() => navigate(`/brand/${routeBrandId}/data-views`)} className="rounded-lg px-3 py-2 text-left text-sm text-slate-700 transition hover:bg-white/70">Data Views</button>
            <button type="button" onClick={() => navigate(`/brand/${routeBrandId}/analytics`)} className="rounded-lg px-3 py-2 text-left text-sm text-slate-700 transition hover:bg-white/70">SEO &amp; Analytics</button>
          </div>
        </div>
        <div className="mb-5">
          <div className="mb-2 flex items-center justify-between">
            <p className={EYEBROW}>Open now</p>
            <span className="font-data text-[11px] text-slate-400">{openWorkTabs.length}</span>
          </div>
          <div className="space-y-1">
            {openWorkTabs.length > 0 ? (
              openWorkTabs.map((tab) => (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => openOrNavigate(tab)}
                  className={`w-full rounded-lg px-3 py-2 text-left text-sm transition ${
                    activeId === tab.id ? "bg-white text-slate-900 shadow-sm ring-1 ring-slate-200" : "text-slate-700 hover:bg-white/70"
                  }`}
                >
                  <span className="truncate">{tab.label}</span>
                </button>
              ))
            ) : (
              <p className="px-3 py-2 text-sm text-slate-400">No active work tabs yet.</p>
            )}
          </div>
        </div>
      </div>
    </aside>
  );
}

function DocsPanel() {
  const location = useLocation();
  const navigate = useNavigate();
  const { activeBrand } = useActiveBrand();
  const { openTab } = useWorkspaceTabs();
  const brandId = parseBrandIdFromPath(location.pathname) ?? activeBrand?.id ?? null;
  // Resolve the display name from the URL brand (docs is brand-scoped), not the
  // active brand — they can differ on /brand/:otherId/docs.
  const { data: brandData, isLoading: brandLoading } = useBrand(brandId ?? undefined);
  const resolvedBrand = brandData?.data;
  const brandName = resolvedBrand?.name ?? activeBrand?.name ?? "Brand";
  const { docs, docsReadyCount, docsLoading } = useBrandDocsList(brandId);
  const currentPath = location.pathname;

  function openDoc(doc: BrandDoc) {
    if (!brandId || !doc.href) return;
    openTab({ id: `artifact:${doc.id}:${brandId}`, kind: "artifact", label: doc.title, route: doc.href });
    navigate(doc.href);
  }

  return (
    <aside className={PANEL_CLASS}>
      <div className="border-b border-slate-200 px-4 py-4">
        <p className={EYEBROW}>Brand Docs</p>
        <h2 className="mt-1 truncate text-xl font-semibold text-slate-900">
          {brandLoading && !resolvedBrand ? "Loading brand…" : brandName}
        </h2>
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto px-3 py-4">
        <div className="mb-5">
          <div className="mb-2 flex items-center justify-between">
            <p className={EYEBROW}>Canonical</p>
            <span className="font-data text-[11px] text-slate-400">
              {docsLoading ? "Loading…" : `${docsReadyCount} ready`}
            </span>
          </div>
          <div className="space-y-1">
            {docs.map((doc) => (
              <button
                key={doc.id}
                type="button"
                disabled={!doc.href}
                onClick={() => openDoc(doc)}
                className={`w-full rounded-lg px-3 py-2 text-left transition disabled:cursor-not-allowed disabled:opacity-60 ${
                  doc.href === currentPath
                    ? "bg-white shadow-sm ring-1 ring-slate-200"
                    : "hover:bg-white/70"
                }`}
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="truncate text-sm font-medium text-slate-800">{doc.title}</span>
                  <span className="font-data text-[10px] text-slate-400">
                    {doc.revision ? `v${doc.revision}` : doc.status}
                  </span>
                </div>
                <p className="mt-1 truncate text-xs text-slate-500">{doc.status}</p>
              </button>
            ))}
          </div>
        </div>
        <div className="rounded-lg border border-slate-200 bg-white px-3 py-3 text-sm text-slate-600">
          Open one document at a time, read it first, then edit only when you want to revise the saved brand guidance.
        </div>
      </div>
    </aside>
  );
}

/**
 * Workflows queues. Lifted verbatim from WorkflowsPage's `sidebar` prop — it held no page
 * state, which is why this section could move without a page rewrite. Chat now has a
 * separate thin thread browser here; its streaming and mutation state remains page-owned.
 * Tasks uses the route's URL-backed filter state, while analytics/assets use dedicated browsers.
 * See PRD-0096 §A.9.
 */
function WorkflowsPanel() {
  const { activeBrand } = useActiveBrand();
  const location = useLocation();
  const brandId = parseBrandIdFromPath(location.pathname) ?? activeBrand?.id ?? null;
  return (
    <aside className="flex h-full w-[252px] flex-col border-r border-slate-200 bg-slate-50/80">
      <div className="border-b border-slate-200 px-4 py-4">
        <p className="font-mono text-[11px] uppercase tracking-[0.08em] text-slate-500">Operations</p>
        <h2 className="mt-1 text-xl font-semibold text-slate-900">Brand work</h2>
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto px-3 py-4">
        {brandId ? <nav className="space-y-2" aria-label="Brand work"><Link className="block rounded-lg px-3 py-2 text-sm font-medium text-brand-700" to={`/brand/${encodeURIComponent(brandId)}/campaigns`}>Campaigns</Link><Link className="block rounded-lg px-3 py-2 text-sm text-slate-700" to={`/runs?brandId=${encodeURIComponent(brandId)}`}>Runs</Link></nav> : <p className="text-sm text-slate-600">Choose a brand to see its campaigns and Runs.</p>}
      </div>
    </aside>
  );
}
